import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Rating } from "primereact/rating";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { useToast } from "../../utils/toast";
import dynamicSurveyService, {
  SurveyAnalysisQuestion,
  SurveyAnalysisDto,
} from "../../services/forms/dynamic.service";

const QUESTION_TYPES = [
  { label: "Serbest Metin", value: "text", icon: "pi pi-align-left", color: "var(--purple-500)" },
  { label: "Tarih", value: "date", icon: "pi pi-calendar", color: "var(--cyan-500)" },
  { label: "Sayı", value: "number", icon: "pi pi-hashtag", color: "var(--orange-500)" },
  { label: "Tek Seçmeli", value: "single_choice", icon: "pi pi-circle-fill", color: "var(--blue-500)" },
  { label: "Açılır Liste", value: "dropdown", icon: "pi pi-chevron-down", color: "var(--teal-500)" },
  { label: "Çok Seçmeli", value: "multiple_choice", icon: "pi pi-check-square", color: "var(--indigo-500)" },
  { label: "Derecelendirme (1-5)", value: "rating", icon: "pi pi-star-fill", color: "var(--yellow-500)" },
];

const TYPE_LABELS: Record<string, string> = {
  text: "Serbest Metin",
  date: "Tarih",
  number: "Sayı",
  single_choice: "Tek Seçmeli",
  dropdown: "Açılır Liste",
  multiple_choice: "Çok Seçmeli",
  rating: "Derecelendirme (1-5)",
};

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return dateStr;
  }
};

const DynamikAnketAnaliz = () => {
  const { id } = useParams<{ id: string }>();
  const surveyId = Number(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedSection, setSelectedSection] = useState<string>("ALL");

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["dynamicSurveyAnalysis", surveyId],
    queryFn: () => dynamicSurveyService.getSurveyAnalysis(surveyId),
    enabled: !!surveyId,
  });

  const analysis: SurveyAnalysisDto | undefined = data?.result?.data;

  // Bölümleri sorular üzerinden veya survey metadata'dan çıkart
  const sectionOptions = useMemo(() => {
    if (!analysis?.questions) return [{ label: "Tüm Bölümler", value: "ALL" }];
    const uniqueSections = Array.from(
      new Set(analysis.questions.map((q) => q.section_title).filter(Boolean) as string[])
    );
    return [
      { label: `Tüm Bölümler (${analysis.questions.length} Soru)`, value: "ALL" },
      ...uniqueSections.map((title) => {
        const count = analysis.questions.filter((q) => q.section_title === title).length;
        return { label: `${title} (${count} Soru)`, value: title };
      }),
    ];
  }, [analysis]);

  // Filtrelenmiş sorular
  const filteredQuestions = useMemo(() => {
    if (!analysis?.questions) return [];
    if (selectedSection === "ALL") return analysis.questions;
    return analysis.questions.filter((q) => q.section_title === selectedSection);
  }, [analysis, selectedSection]);

  // Soruları bölümlere göre gruplama (Tüm bölümler seçildiğinde görsel kartlar)
  const groupedQuestions = useMemo(() => {
    if (!analysis?.questions) return {};
    const groups: Record<string, SurveyAnalysisQuestion[]> = {};
    for (const q of filteredQuestions) {
      const section = q.section_title || "Genel Sorular";
      if (!groups[section]) groups[section] = [];
      groups[section].push(q);
    }
    return groups;
  }, [filteredQuestions, analysis]);

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="p-3 md:p-4" style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <div className="surface-card border-round-xl shadow-1 border-1 surface-border p-6 text-center">
          <i className="pi pi-exclamation-triangle text-yellow-500 mb-3" style={{ fontSize: "2.5rem" }} />
          <h3 className="text-900 font-bold text-xl mb-2">Analiz Verisi Bulunamadı</h3>
          <p className="text-500 text-sm mb-4">
            İstenen ankete ait analiz verisi alınamadı veya anket mevcut değil.
          </p>
          <Button
            label="Anket Listesine Dön"
            icon="pi pi-arrow-left"
            outlined
            onClick={() => navigate("/admin/dinamik-anket-yonetim")}
          />
        </div>
      </div>
    );
  }

  const shareUrl = analysis.share_token
    ? `${window.location.origin}/anket/${analysis.share_token}`
    : `${window.location.origin}/anketler/${surveyId}`;

  const renderQuestionResult = (question: SurveyAnalysisQuestion) => {
    // 1. Serbest Metin
    if (["text", "date"].includes(question.question_type)) {
      const results: { user_email: string; user_osb: string; text_answer: string; submitted_at: string }[] =
        question.results ?? [];

      return (
        <div className="overflow-x-auto">
          <DataTable
            value={results}
            stripedRows
            emptyMessage="Henüz serbest metin cevabı verilmemiş."
            size="small"
            paginator={results.length > 5}
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            className="border-round-lg overflow-hidden border-1 surface-border"
            tableStyle={{ minWidth: "500px" }}
          >
            <Column
              field="user_email"
              header="Katılımcı"
              body={(row) => (
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-user text-primary text-xs" />
                  <span className="font-medium text-900 text-sm">{row.user_email}</span>
                </div>
              )}
              style={{ width: "220px" }}
            />
            <Column
              field="user_osb"
              header="OSB"
              body={(row) => <Tag value={row.user_osb} style={{ fontSize: "0.75rem" }} />}
              style={{ width: "160px" }}
            />
            <Column
              field="text_answer"
              header="Cevap"
              body={(row) => (
                <div className="p-2 border-round surface-50 text-sm text-800" style={{ whiteSpace: "pre-wrap", lineHeight: "1.4" }}>
                  {row.text_answer}
                </div>
              )}
            />
            <Column
              field="submitted_at"
              header="Tarih"
              body={(row) => (
                <span className="text-500 text-xs flex align-items-center gap-1">
                  <i className="pi pi-clock text-xs" />
                  {row.submitted_at}
                </span>
              )}
              style={{ width: "150px" }}
            />
          </DataTable>
        </div>
      );
    }

    // 2. Seçenekli Sorular (Tek Seçmeli, Çok Seçmeli, Açılır Liste)
    if (["single_choice", "dropdown", "multiple_choice"].includes(question.question_type)) {
      const results: { id: number; text: string; count: number; percentage: number }[] = question.results ?? [];
      const maxCount = Math.max(...results.map((r) => r.count), 0);

      return (
        <div className="flex flex-column gap-2">
          {results.map((choice, cIdx) => (
            <div
              key={choice.id ?? cIdx}
              className="p-3 border-round-lg surface-50 border-1 surface-border transition-duration-150"
            >
              <div className="flex align-items-center justify-content-between mb-2">
                <div className="flex align-items-center gap-2">
                  <i
                    className={
                      question.question_type === "multiple_choice"
                        ? "pi pi-check-square text-primary text-sm"
                        : "pi pi-circle-fill text-primary text-xs"
                    }
                  />
                  <span className="text-900 font-medium text-sm">{choice.text}</span>
                </div>
                <div className="flex align-items-center gap-2">
                  <span className="text-600 text-xs font-semibold">{choice.count} yanıt</span>
                  <Tag
                    value={`%${choice.percentage}`}
                    severity={choice.count === maxCount && choice.count > 0 ? "success" : "info"}
                    style={{ fontSize: "0.75rem" }}
                  />
                </div>
              </div>
              <ProgressBar
                value={choice.percentage}
                showValue={false}
                style={{ height: "8px", borderRadius: "4px" }}
                color={choice.count === maxCount && choice.count > 0 ? "var(--primary-color)" : "var(--blue-300)"}
              />
            </div>
          ))}
          {results.length === 0 && (
            <div className="text-center py-3 text-500 text-sm">
              <i className="pi pi-info-circle mr-1" />
              Henüz bu soruya yanıt verilmemiş.
            </div>
          )}
        </div>
      );
    }

    // 3. Derecelendirme (1-5 Puanlama)
    if (question.question_type === "rating") {
      const result: { average: number; distribution: Record<string, number>; count: number } =
        question.results ?? { average: 0, distribution: {}, count: 0 };

      return (
        <div className="p-3 border-round-xl surface-50 border-1 surface-border">
          <div className="grid align-items-center">
            <div className="col-12 md:col-4 flex flex-column align-items-center justify-content-center border-bottom-1 md:border-bottom-none md:border-right-1 surface-border pb-3 md:pb-0">
              <span className="text-5xl font-bold text-primary mb-1">{result.average}</span>
              <span className="text-500 text-sm">5 üzerinden ortalama</span>
              <Rating value={Math.round(result.average)} readOnly cancel={false} stars={5} className="mt-2" />
              <Tag
                value={`Toplam ${result.count} değerlendirme`}
                severity="info"
                className="mt-2"
                style={{ fontSize: "0.75rem" }}
              />
            </div>
            <div className="col-12 md:col-8 pl-0 md:pl-4">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = result.distribution?.[String(star)] ?? 0;
                const pct = result.count > 0 ? Math.round((count / result.count) * 100) : 0;
                return (
                  <div key={star} className="flex align-items-center gap-2 mb-2">
                    <span className="text-700 font-medium text-xs flex align-items-center gap-1" style={{ width: "45px" }}>
                      <span>{star}</span>
                      <i className="pi pi-star-fill text-yellow-500 text-xs" />
                    </span>
                    <ProgressBar
                      value={pct}
                      showValue={false}
                      style={{ flex: 1, height: "10px", borderRadius: "5px" }}
                      color={star >= 4 ? "var(--green-500)" : star === 3 ? "var(--yellow-500)" : "var(--red-400)"}
                    />
                    <span className="text-600 font-semibold text-xs text-right" style={{ width: "35px" }}>
                      %{pct}
                    </span>
                    <span className="text-500 text-xs text-right" style={{ width: "50px" }}>
                      ({count})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (question.question_type === "number") {
      const result: { average: number; minimum: number | null; maximum: number | null; count: number } = question.results ?? { average: 0, minimum: null, maximum: null, count: 0 };
      return (
        <div className="grid m-0 surface-50 border-round-xl border-1 surface-border p-3 text-center">
          <div className="col-6 md:col-3"><span className="block text-500 text-xs mb-1">Yanıt sayısı</span><strong className="text-900 text-xl">{result.count}</strong></div>
          <div className="col-6 md:col-3"><span className="block text-500 text-xs mb-1">Ortalama</span><strong className="text-900 text-xl">{result.average}</strong></div>
          <div className="col-6 md:col-3"><span className="block text-500 text-xs mb-1">En düşük</span><strong className="text-900 text-xl">{result.minimum ?? "–"}</strong></div>
          <div className="col-6 md:col-3"><span className="block text-500 text-xs mb-1">En yüksek</span><strong className="text-900 text-xl">{result.maximum ?? "–"}</strong></div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "1080px", margin: "0 auto" }}>
      {/* ── Üst Başlık & Eylem Çubuğu ─────────────────────────────────── */}
      <div className="surface-card border-round-xl shadow-1 p-3 md:p-4 mb-4 border-1 surface-border">
        <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
          <div>
            <Button
              icon="pi pi-arrow-left"
              label="Anket Listesine Dön"
              text
              severity="secondary"
              className="p-0 mb-2 font-medium"
              onClick={() => navigate("/admin/dinamik-anket-yonetim")}
            />
            <div className="flex align-items-center gap-2 flex-wrap">
              <h1 className="text-900 font-bold text-2xl m-0">{analysis.survey_title}</h1>
              <Tag
                value={analysis.is_active ? "Yayında (Aktif)" : "Pasif / Taslak"}
                severity={analysis.is_active ? "success" : "warning"}
                icon={analysis.is_active ? "pi pi-check" : "pi pi-eye-slash"}
              />
              <Tag
                value={`${analysis.total_responses} Yanıt Alındı`}
                severity="info"
                icon="pi pi-users"
              />
              <Tag
                value="Analiz & Rapor"
                icon="pi pi-chart-bar"
              />
            </div>
            <p className="text-500 text-sm mt-1 mb-0">
              {analysis.survey_description ||
                "Ankete verilen yanıtların detaylı istatistikleri, soru bazlı dağılımları ve katılım raporu."}
            </p>
          </div>

          <div className="flex align-items-center gap-2 w-full md:w-auto justify-content-end flex-wrap">
            <Button
              label="Anketi Düzenle"
              icon="pi pi-pencil"
              outlined
              severity="secondary"
              onClick={() => navigate(`/admin/dinamik-anket-olustur/${surveyId}`)}
            />
            <Button
              label="Yenile"
              icon="pi pi-refresh"
              loading={isFetching}
              onClick={() => {
                void refetch();
                toast.show("Analiz verileri güncellendi.", "info");
              }}
              className="p-button-primary font-semibold shadow-1"
            />
          </div>
        </div>

        {/* İstatistik Rozetleri (Dinamik Anket Oluştur sayfasıyla birebir uyumlu) */}
        <div className="flex align-items-center gap-3 mt-3 pt-3 border-top-1 surface-border text-sm text-600 flex-wrap">
          <div className="flex align-items-center gap-1">
            <i className="pi pi-folder text-primary" />
            <span className="font-semibold text-900">{analysis.sections_count ?? 1}</span> Bölüm
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i className="pi pi-list text-primary" />
            <span className="font-semibold text-900">{analysis.questions.length}</span> Soru
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i className="pi pi-users text-primary" />
            <span>Kontenjan: </span>
            <span className="font-semibold text-900">
              {analysis.response_limit ? `${analysis.response_limit} kişi` : "Sınırsız"}
            </span>
          </div>
          <span>•</span>
          <div className="flex align-items-center gap-1">
            <i
              className={
                analysis.access_mode === "public" ? "pi pi-globe text-green-500" : "pi pi-lock text-orange-500"
              }
            />
            <span>{analysis.access_mode === "public" ? "Bağlantıya Açık" : "Giriş Zorunlu"}</span>
          </div>
          {analysis.is_anonymous && (
            <>
              <span>•</span>
              <div className="flex align-items-center gap-1">
                <i className="pi pi-eye-slash text-purple-500" />
                <span>Anonim Yanıtlar</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Özet KPI Kartları ────────────────────────────────────────── */}
      <div className="grid mb-4">
        {/* Kart 1: Toplam Yanıt */}
        <div className="col-12 sm:col-6 lg:col-4">
          <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="text-500 font-medium text-sm">Toplam Katılım</span>
              <div
                className="border-circle bg-blue-50 text-blue-600 flex align-items-center justify-content-center"
                style={{ width: "38px", height: "38px" }}
              >
                <i className="pi pi-users font-bold text-lg" />
              </div>
            </div>
            <div className="text-900 font-bold text-3xl mb-1">{analysis.total_responses}</div>
            <span className="text-500 text-xs">
              {analysis.response_limit ? `Kontenjan: ${analysis.response_limit} kişi` : "Sınırsız katılım hakkı"}
            </span>
          </Card>
        </div>

        {/* Kart 2: Toplam Soru */}
        <div className="col-12 sm:col-6 lg:col-4">
          <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="text-500 font-medium text-sm">Toplam Soru Sayısı</span>
              <div
                className="border-circle bg-teal-50 text-teal-600 flex align-items-center justify-content-center"
                style={{ width: "38px", height: "38px" }}
              >
                <i className="pi pi-list font-bold text-lg" />
              </div>
            </div>
            <div className="text-900 font-bold text-3xl mb-1">{analysis.questions.length}</div>
            <span className="text-500 text-xs">{analysis.sections_count ?? 1} anket bölümü içerisinde</span>
          </Card>
        </div>

        {/* Kart 3: Kontenjan & Durum */}
        <div className="col-12 sm:col-12 lg:col-4">
          <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="text-500 font-medium text-sm">Katılım & Doluluk</span>
              <div
                className="border-circle bg-purple-50 text-purple-600 flex align-items-center justify-content-center"
                style={{ width: "38px", height: "38px" }}
              >
                <i className="pi pi-chart-pie font-bold text-lg" />
              </div>
            </div>
            <div className="text-900 font-bold text-3xl mb-1">
              {analysis.response_limit
                ? `%${Math.min(100, Math.round((analysis.total_responses / analysis.response_limit) * 100))}`
                : analysis.total_responses > 0
                ? "Aktif"
                : "0"}
            </div>
            <span className="text-500 text-xs">
              {analysis.response_limit
                ? `${analysis.total_responses} / ${analysis.response_limit} yanıt alındı`
                : analysis.is_active
                ? "Yeni katılımlar kabul ediliyor"
                : "Anket yanıt alımına kapalı"}
            </span>
          </Card>
        </div>
      </div>

      {/* ── Sekmeli Görünüm (Oluşturma Sayfası ile Tam Simetrik) ──────── */}
      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} className="custom-tabview">
        {/* SEKME 1: SORU BAZLI ANALİZLER */}
        <TabPanel header="Soru Analizleri" leftIcon="pi pi-chart-bar mr-2">
          {analysis.total_responses === 0 ? (
            <div className="surface-card border-round-xl shadow-1 border-1 surface-border p-6 text-center mt-3">
              <div
                className="border-circle surface-100 inline-flex align-items-center justify-content-center mb-3"
                style={{ width: "64px", height: "64px" }}
              >
                <i className="pi pi-inbox text-400 text-3xl" />
              </div>
              <h3 className="text-900 font-bold text-xl mb-2">Henüz Yanıt Alınmadı</h3>
              <p className="text-500 text-sm max-w-26rem mx-auto mb-4">
                Bu ankete henüz hiçbir katılımcı yanıt vermedi. Anket bağlantısını katılımcılarla paylaşarak yanıt toplamaya
                başlayabilirsiniz.
              </p>
              <div className="flex justify-content-center gap-2">
                <Button
                  label="Anketi Düzenle"
                  icon="pi pi-pencil"
                  outlined
                  severity="secondary"
                  onClick={() => navigate(`/admin/dinamik-anket-olustur/${surveyId}`)}
                />
                <Button
                  label="Yenile"
                  icon="pi pi-refresh"
                  onClick={() => {
                    void refetch();
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="mt-3 flex flex-column gap-4">
              {/* Filtreleme ve Başlık Çubuğu */}
              {sectionOptions.length > 2 && (
                <div className="surface-card p-3 border-round-xl border-1 surface-border shadow-1 flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-filter text-primary" />
                    <span className="font-semibold text-900 text-sm">Bölüme Göre Filtrele:</span>
                  </div>
                  <Dropdown
                    value={selectedSection}
                    options={sectionOptions}
                    onChange={(e) => setSelectedSection(e.value)}
                    className="w-full sm:w-20rem p-inputtext-sm"
                  />
                </div>
              )}

              {/* Bölüm Blokları ve Soru Kartları */}
              {Object.entries(groupedQuestions).map(([sectionTitle, sectionQuestions]) => (
                <div
                  key={sectionTitle}
                  className="surface-card border-round-xl shadow-1 border-1 surface-border overflow-hidden"
                >
                  {/* Bölüm Başlık Şeridi */}
                  <div className="p-3 md:p-4 surface-50 border-bottom-1 surface-border flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-2">
                      <i className="pi pi-folder text-primary font-bold text-lg" />
                      <h2 className="text-900 font-semibold text-lg m-0">{sectionTitle}</h2>
                      <Tag value={`${sectionQuestions.length} Soru`} severity="info" />
                    </div>
                  </div>

                  {/* Soruların Listesi */}
                  <div className="p-3 md:p-4 flex flex-column gap-3">
                    {sectionQuestions.map((q, qIndex) => {
                      // Global veya bölüm içi indeks
                      const displayIndex =
                        analysis.questions.findIndex((origQ) => origQ.id === q.id) !== -1
                          ? analysis.questions.findIndex((origQ) => origQ.id === q.id) + 1
                          : qIndex + 1;

                      return (
                        <div
                          key={q.id}
                          className="p-3 md:p-4 surface-card border-round-xl border-1 surface-border shadow-1 hover:shadow-2 transition-duration-150"
                          style={{ borderLeft: "5px solid var(--primary-color)" }}
                        >
                          <div className="flex align-items-start justify-content-between gap-2 mb-3">
                            <div className="flex align-items-start gap-3 flex-1">
                              <span
                                className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0 text-white shadow-1"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  minWidth: "32px",
                                  background: "var(--primary-color)",
                                }}
                              >
                                {displayIndex}
                              </span>
                              <div className="flex-1">
                                <div className="flex align-items-center gap-2 flex-wrap mb-1">
                                  <Tag
                                    value={TYPE_LABELS[q.question_type] ?? q.question_type}
                                    icon={QUESTION_TYPES.find((t) => t.value === q.question_type)?.icon ?? "pi pi-question"}
                                    severity="info"
                                    style={{ fontSize: "0.75rem" }}
                                  />
                                  <Tag
                                    value={`${q.respondent_count ?? 0} Yanıt`}
                                    icon="pi pi-check"
                                    severity="success"
                                    style={{ fontSize: "0.75rem" }}
                                  />
                                </div>

                                <p className="text-900 font-semibold m-0 text-base" style={{ lineHeight: "1.5" }}>
                                  {q.text}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Sonuçların Gösterimi */}
                          <div className="pl-0 md:pl-5 mt-3 pt-3 border-top-1 surface-border">
                            {renderQuestionResult(q)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabPanel>

        {/* SEKME 2: GENEL BİLGİLER & PAYLAŞIM */}
        <TabPanel header="Genel Bilgiler & Paylaşım" leftIcon="pi pi-cog mr-2">
          <div className="grid mt-2">
            {/* Sol Kart: Temel Anket Bilgileri ve Paylaşım */}
            <div className="col-12 lg:col-7">
              <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-file-edit text-primary font-bold text-xl" />
                  <h3 className="text-900 font-semibold text-lg m-0">Temel Bilgiler & Paylaşım</h3>
                </div>

                <div className="flex flex-column gap-3">
                  <div>
                    <label className="block text-500 font-medium mb-1 text-sm">Anket Başlığı</label>
                    <div className="text-900 font-semibold text-lg">{analysis.survey_title}</div>
                  </div>

                  <div>
                    <label className="block text-500 font-medium mb-1 text-sm">Açıklama / Yönerge</label>
                    <p className="text-700 m-0 text-sm line-height-3">
                      {analysis.survey_description || "Açıklama girilmemiş."}
                    </p>
                  </div>

                  <Divider className="my-1" />

                  {/* Paylaşım Bağlantısı */}
                  <div>
                    <label className="block text-900 font-semibold mb-1 text-sm">Paylaşım Bağlantısı</label>
                    <small className="text-500 block mb-2">
                      {analysis.access_mode === "public"
                        ? "Bu bağlantıyı kopyalayarak herkese gönderebilirsiniz."
                        : "Bu bağlantı ile yalnızca giriş yapmış kullanıcılar ankete katılabilir."}
                    </small>
                    <div className="flex align-items-center gap-2">
                      <InputText
                        readOnly
                        value={shareUrl}
                        className="w-full surface-100 font-mono text-sm"
                      />
                      <Button
                        icon="pi pi-copy"
                        label="Kopyala"
                        severity="secondary"
                        onClick={() => {
                          void navigator.clipboard.writeText(shareUrl);
                          toast.show("Paylaşım bağlantısı panoya kopyalandı.", "info");
                        }}
                      />
                      <Button
                        icon="pi pi-external-link"
                        tooltip="Yeni sekmede aç"
                        outlined
                        severity="secondary"
                        onClick={() => window.open(shareUrl, "_blank")}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sağ Kart: Erişim & Katılım Kuralları */}
            <div className="col-12 lg:col-5">
              <Card className="h-full border-round-xl shadow-1 surface-card border-1 surface-border">
                <div className="flex align-items-center gap-2 mb-3">
                  <i className="pi pi-shield text-primary font-bold text-xl" />
                  <h3 className="text-900 font-semibold text-lg m-0">Erişim & Katılım Parametreleri</h3>
                </div>

                <div className="flex flex-column gap-3">
                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900 text-sm">Erişim İzni</span>
                      <small className="text-500">
                        {analysis.access_mode === "public" ? "Bağlantıya Sahip Herkes" : "Yalnızca Giriş Yapmış Kullanıcılar"}
                      </small>
                    </div>
                    <Tag
                      value={analysis.access_mode === "public" ? "Herkese Açık" : "Giriş Zorunlu"}
                      severity={analysis.access_mode === "public" ? "success" : "warning"}
                    />
                  </div>

                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900 text-sm">Yayın Durumu</span>
                      <small className="text-500">
                        {analysis.is_active ? "Aktif ve yanıt kabul ediyor." : "Pasif/Kapalı modda."}
                      </small>
                    </div>
                    <Tag
                      value={analysis.is_active ? "Aktif" : "Pasif"}
                      severity={analysis.is_active ? "success" : "danger"}
                    />
                  </div>

                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900 text-sm">Katılımcı Kimliği</span>
                      <small className="text-500">
                        {analysis.is_anonymous
                          ? "Katılımcı hesap bilgisi gizlenir."
                          : analysis.access_mode === "public"
                            ? "Giriş yapanların hesap bilgisi kaydedilir; giriş yapmadan katılanların kimliği belirlenemez."
                            : "Giriş yapan katılımcıların hesap bilgisi kaydedilir."}
                      </small>
                    </div>
                    <Tag
                      value={
                        analysis.is_anonymous
                          ? "Anonim"
                          : analysis.access_mode === "public"
                            ? "Giriş yapanlar tanımlı"
                            : "Hesapla tanımlı"
                      }
                      severity={analysis.is_anonymous ? "info" : analysis.access_mode === "public" ? "warning" : undefined}
                    />
                  </div>

                  <div className="p-3 border-round-lg surface-50 border-1 surface-border flex align-items-center justify-content-between">
                    <div>
                      <span className="block font-semibold text-900 text-sm">Kontenjan (Maks. Yanıt)</span>
                      <small className="text-500">
                        {analysis.response_limit
                          ? `Üst sınır: ${analysis.response_limit} kişi (${analysis.total_responses} doldu)`
                          : "Herhangi bir üst sınır bulunmuyor."}
                      </small>
                    </div>
                    <span className="font-bold text-900">
                      {analysis.response_limit ? `${analysis.response_limit} kişi` : "Sınırsız"}
                    </span>
                  </div>

                  <div className="grid mt-1">
                    <div className="col-6">
                      <span className="block text-500 text-xs mb-1">Başlangıç Tarihi</span>
                      <span className="text-900 font-medium text-sm">{formatDate(analysis.starts_at)}</span>
                    </div>
                    <div className="col-6">
                      <span className="block text-500 text-xs mb-1">Bitiş Tarihi</span>
                      <span className="text-900 font-medium text-sm">{formatDate(analysis.ends_at)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default DynamikAnketAnaliz;
